import React, { useState } from 'react';

interface CopyRefinementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (copy: { headline: string; description: string; cta: string }) => void;
  initialCopy: {
    headline: string;
    description: string;
    cta: string;
  };
}

export const CopyRefinementModal: React.FC<CopyRefinementModalProps> = ({
  isOpen,
  onClose,
  onApply,
  initialCopy
}) => {
  const [headline, setHeadline] = useState(initialCopy.headline);
  const [description, setDescription] = useState(initialCopy.description);
  const [cta, setCta] = useState(initialCopy.cta);

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({ headline, description, cta });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-boxdark rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-stroke">
          <div className="flex justify-between items-center">
            <h3 className="text-2xl font-semibold">✏️ Refine AI Copy</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Tweak the AI-generated copy before applying it to your campaign
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Headline <span className="text-gray-400">({headline.length}/40)</span>
            </label>
            <input
              type="text"
              value={headline}
              onChange={(e) => setHeadline(e.target.value.slice(0, 40))}
              maxLength={40}
              className="w-full px-4 py-2 border border-stroke rounded-lg dark:bg-boxdark focus:border-primary focus:outline-none"
              autoFocus
            />
            <div className="mt-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  headline.length > 35 ? 'bg-red-500' : headline.length > 30 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${(headline.length / 40) * 100}%` }}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Description <span className="text-gray-400">({description.length}/125)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 125))}
              maxLength={125}
              rows={4}
              className="w-full px-4 py-2 border border-stroke rounded-lg dark:bg-boxdark focus:border-primary focus:outline-none"
            />
            <div className="mt-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  description.length > 110 ? 'bg-red-500' : description.length > 90 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${(description.length / 125) * 100}%` }}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Call to Action <span className="text-gray-400">({cta.length}/20)</span>
            </label>
            <input
              type="text"
              value={cta}
              onChange={(e) => setCta(e.target.value.slice(0, 20))}
              maxLength={20}
              className="w-full px-4 py-2 border border-stroke rounded-lg dark:bg-boxdark focus:border-primary focus:outline-none"
            />
            <div className="mt-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  cta.length > 17 ? 'bg-red-500' : cta.length > 14 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${(cta.length / 20) * 100}%` }}
              />
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-2">💡 Preview</h4>
            <div className="space-y-2">
              <p className="font-bold">{headline || '(No headline)'}</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{description || '(No description)'}</p>
              <button className="px-4 py-2 bg-primary text-white rounded text-sm font-medium">
                {cta || 'Click Here'}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-stroke flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-stroke rounded-lg hover:bg-gray-50 dark:hover:bg-meta-4 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 font-medium"
          >
            ✓ Apply Refined Copy
          </button>
        </div>
      </div>
    </div>
  );
};
